import { HttpParams } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { ActivatedRouteSnapshot, Params, Router } from '@angular/router';
import { WipperozAlertService } from 'app/wipperoz-core/services/alert.service';
import { LoggingService } from 'app/wipperoz-core/services/logging.service';
import { WipperozApiService } from 'app/wipperoz-core/services/wipperoz-api.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BlogContent } from './types/blog-content.model';

import { isNil, propEq, find, isEmpty } from 'ramda';
import { QueryOptions } from 'app/wipperoz-core/types/content/query-options.model';
import { Tags } from './types/tags.model';
@Injectable({
  providedIn: 'root'
})
export class BlogService {
  onResultsChanged: BehaviorSubject<any>;
  language = 'EN';
  getAllData = false;
  routeParams: Params;
  content: BlogContent[] = [];
  latestContent: BlogContent[] = [];
  tags: Tags[] = [];
  totalCount = 0;
  blogTitle = $localize`:@@blogTitle:Wipperoz blog`;
  blogSubtitle = $localize`:@@blogSubTitle:Digital content just for you; helpful tips, best life hacks and creative ideas to inspire you.`;

  options: QueryOptions = {
    page: 1,
    searchCriteria: '',
    pageSize: 6
  };

  constructor(
    private _wipperozApiService: WipperozApiService,
    private wipperozAlertService: WipperozAlertService,
    private loggingService: LoggingService,
    private router: Router,

    @Inject(LOCALE_ID) private localeId: string
  ) {
    this.onResultsChanged = new BehaviorSubject<any>(null);
  }

  resolve(route: ActivatedRouteSnapshot): Observable<any> | Promise<any> | any {
    this.routeParams = route.params;
    return new Promise((resolve, reject) => {
      this.getContent().then((data) => {
        resolve({ blogContent: data });
      }, reject);
    });
  }

  getContent(): Promise<BlogContent> {
    const id = this.routeParams !== undefined ? this.routeParams.id : 0;
    return new Promise((resolve) => {
      let response = [];
      if (this.content !== undefined && this.content.length > 0) {
        response = this.content.filter((item) => {
          return item.contentSlug === id;
        });
        if (response.length > 0) {
          resolve(response[0]);
        } else {
          this.getBlogContentBySlug(id).then((data: BlogContent) => {
            resolve(data);
          });
        }
      } else {
        this.getBlogContentBySlug(id).then((data: BlogContent) => {
          resolve(data);
        });
      }
    });
  }

  getBlogContentBySlug(slug): Promise<any> {
    let params = new HttpParams();

    params = params.append('slug', slug);

    return new Promise((resolve, reject) => {
      this._wipperozApiService
        .get('/resources/blog-content-by-slug', params)
        .pipe(
          filter((response: any) => {
            return response !== undefined;
          })
        )
        .subscribe(
          (response: any) => {
            this.content = response[0];
            resolve(response[0]);
          },
          (err) => {
            if (err.error.errorType === 'IncorrectSlug') {
              this.router.navigate(['/blog']);
            } else {
              this.loggingService.logError('blog.service: getBlogContent error', JSON.stringify(err));
              this.wipperozAlertService.error(
                $localize`:@@errorGettingBlogContentMessage:Error getting blog content. We are investigating the cause.`,
                false,
                10000
              );
            }
          }
        ),
        (err) => {
          of(`I caught: ${err}`);
          return reject;
        };
    });
  }

  getBlogContentByTag(options: QueryOptions, tag: string): Promise<BlogContent[]> {
    let params = new HttpParams();

    params = params.append('language', this.localeId);
    params = params.append('pageSize', options.pageSize);
    params = params.append('page', options.page);
    params = params.append('tag', tag);
    params = params.append('searchCriteria', !isNil(options.searchCriteria) ? options.searchCriteria : '');

    return new Promise((resolve, reject) => {
      this._wipperozApiService
        .get('/resources/blog-content-by-tag', params)
        .pipe(filter((response: any) => response !== undefined))
        .subscribe(
          (response: any) => {
            if (isNil(response)) {
              this.onResultsChanged.next([]);
              resolve([]);
            } else {
              this.content = [];
              response.items.map((item: BlogContent) => {
                if (!find(propEq('contentId', item.contentId), this.content)) {
                  this.content.push(item);
                }
              });
              this.totalCount = response.totalCount;
              this.onResultsChanged.next(this.content);
              resolve(this.content);
            }
          },
          (err) => {
            this.loggingService.logError('blog.service: getBlogContent error', JSON.stringify(err));
            this.wipperozAlertService.error(
              $localize`:@@errorGettingBlogContentMessage:Error getting blog content. We are investigating the cause.`,
              false,
              10000
            );
          }
        ),
        (err) => {
          of(`I caught: ${err}`);
          return reject;
        };
    });
  }

  getBlogContent(options: QueryOptions): Promise<BlogContent[]> {
    let params = new HttpParams();

    if (isEmpty(this.latestContent) || isEmpty(this.tags)) {
      this.getAllData = true;
    }

    params = params.append('language', this.localeId);
    params = params.append('pageSize', options.pageSize);
    params = params.append('page', options.page);
    params = params.append('getAllData', this.getAllData);
    params = params.append('searchCriteria', !isNil(options.searchCriteria) ? options.searchCriteria : '');

    return new Promise((resolve, reject) => {
      this._wipperozApiService
        .get('/resources/blog-content', params)
        .pipe(filter((response: any) => response !== undefined))
        .subscribe(
          (response: any) => {
            if (isNil(response)) {
              this.onResultsChanged.next([]);
              resolve([]);
            } else {
              this.content = [];
              response.items.map((item: BlogContent) => {
                if (!find(propEq('contentId', item.contentId), this.content)) {
                  this.content.push(item);
                }
              });
              if (this.getAllData) {
                this.latestContent = this.content.slice(0, 3);
                this.tags = response.tags;
                this.tags.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
              }
              this.totalCount = response.totalCount;
              this.onResultsChanged.next(this.content);
              resolve(this.content);
            }
          },
          (err) => {
            this.loggingService.logError('blog.service: getBlogContent error', JSON.stringify(err));
            this.wipperozAlertService.error(
              $localize`:@@errorGettingBlogContentMessage:Error getting blog content. We are investigating the cause.`,
              false,
              10000
            );
          }
        ),
        (err) => {
          of(`I caught: ${err}`);
          return reject;
        };
    });
  }

  getLatestArticles(): Promise<BlogContent[]> {
    return null;
  }
}
